import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import { RecipeDocument } from '../store';

export type ProcessRecipeDialogMode = 'load' | 'save' | 'delete';

export interface ProcessRecipeDialogProps {
  open: boolean;
  mode: ProcessRecipeDialogMode;
  recipes: RecipeDocument[];
  /** Pre-fills the name field in "save" mode (the currently loaded file, if any). */
  defaultName?: string;
  onClose: () => void;
  onLoad: (recipe: RecipeDocument) => void;
  onSave: (name: string) => void;
  onDelete: (name: string) => void;
}

const TITLES: Record<ProcessRecipeDialogMode, string> = {
  load: 'Load process file',
  save: 'Save process file',
  delete: 'Delete process file',
};

/**
 * Operator-facing file picker backing the Process screen's "Load / Save /
 * Delete Process" buttons (see `HMIRenderer`'s `handleRunAction`). Lists
 * every saved recipe from `listRecipes()`/`saveRecipe()`/`deleteRecipe()`
 * (`src/hmi/store.ts`) so picking one fills every parameter field on the
 * Process screen - no "Configure HMI" trip required, since this manages
 * operational data (saved parameter sets), not the template structure.
 */
function ProcessRecipeDialog({
  open,
  mode,
  recipes,
  defaultName,
  onClose,
  onLoad,
  onSave,
  onDelete,
}: ProcessRecipeDialogProps) {
  const [name, setName] = useState(defaultName ?? '');

  useEffect(() => {
    if (open && mode === 'save') setName(defaultName ?? '');
  }, [open, mode, defaultName]);

  const sorted = [...recipes].sort((a, b) => a.name.localeCompare(b.name));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{TITLES[mode]}</DialogTitle>
      <DialogContent>
        {mode === 'save' && (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Saves the current value of every parameter on the Process
              screen under this name, so it can be reloaded later.
            </Typography>
            <TextField
              label="Process file name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              fullWidth
            />
            {sorted.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Existing files (click to overwrite):
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 0.5 }}>
                  {sorted.map((r) => (
                    <Button
                      key={r.name}
                      size="small"
                      variant="outlined"
                      onClick={() => setName(r.name)}
                    >
                      {r.name}
                    </Button>
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
        )}

        {mode !== 'save' && sorted.length === 0 && (
          <Alert severity="info" sx={{ mt: 1 }}>
            No process files saved yet. Use &quot;Save Process&quot; to create one.
          </Alert>
        )}

        {mode !== 'save' && sorted.length > 0 && (
          <List dense>
            {sorted.map((r) => (
              <ListItem
                key={r.name}
                disablePadding={mode === 'load'}
                sx={mode === 'delete' ? { pr: 1 } : undefined}
                secondaryAction={
                  mode === 'delete' ? (
                    <IconButton
                      edge="end"
                      color="error"
                      aria-label={`Delete ${r.name}`}
                      onClick={() => onDelete(r.name)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  ) : undefined
                }
              >
                {mode === 'load' ? (
                  <ListItemButton onClick={() => onLoad(r)}>
                    <ListItemText
                      primary={r.name}
                      secondary={`Saved ${new Date(r.savedAt).toLocaleString()}`}
                    />
                  </ListItemButton>
                ) : (
                  <ListItemText
                    primary={r.name}
                    secondary={`Saved ${new Date(r.savedAt).toLocaleString()}`}
                    sx={{ pr: 5 }}
                  />
                )}
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{mode === 'save' ? 'Cancel' : 'Close'}</Button>
        {mode === 'save' && (
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={!name.trim()}
            onClick={() => onSave(name.trim())}
          >
            Save
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default ProcessRecipeDialog;
